/**
 * PageStatus — what the page area shows instead of a page: while it loads (PageLoading),
 * when it couldn't load or crashed (PageBoundary), or when someone isn't allowed in
 * (PageMessage). The header, menu and footer stay around it.
 */
import React, { useEffect, useState } from 'react';
import { reloadPage } from '../../utils/appVersion';
import { isChunkError } from '../../utils/lazyPage';
import './PageStatus.css';

const SLOW_MS = 300;

export function PageMessage({ icon, title, children = null, action = null }) {
  return (
    <div className="page-status">
      <div className="page-status__icon" aria-hidden="true">{icon}</div>
      <h2 className="page-status__title">{title}</h2>
      {children ? <p className="page-status__text">{children}</p> : null}
      {action}
    </div>
  );
}

/** Suspense fallback for a page: blank for the first 300 ms, so a quick load doesn't flash. */
export function PageLoading() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_MS);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="page-status page-status--loading" role="status">
      {slow ? 'Loading…' : null}
    </div>
  );
}

/**
 * Catches a page that couldn't load or crashed, so the rest of the app keeps working.
 * Going to another address clears it. Nothing is remounted when there was no error, so
 * moving inside a page (an order list → one order) keeps its state and listeners.
 */
export class PageBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    return props.resetKey === state.resetKey ? null : { error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error, info) {
    console.error('[App] page failed:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const notLoaded = isChunkError(error);
    return (
      <PageMessage
        icon={notLoaded ? '📶' : '⚠️'}
        title={notLoaded ? "Couldn't load this page" : 'Something went wrong on this page'}
        action={<button type="button" className="page-status__btn" onClick={() => reloadPage()}>Reload</button>}
      >
        {notLoaded
          ? 'Check the internet connection, then reload.'
          : 'Reload to try again. If it keeps happening, let an admin know.'}
      </PageMessage>
    );
  }
}
